import type {
  ConnectorEndpoint,
  GfxElement,
  IBound,
  IVec,
} from './types';

export function centerOf(b: IBound): IVec {
  return [b.x + b.w / 2, b.y + b.h / 2];
}

export function rotatePoint(point: IVec, center: IVec, rad: number): IVec {
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = point[0] - center[0];
  const dy = point[1] - center[1];
  return [center[0] + dx * cos - dy * sin, center[1] + dx * sin + dy * cos];
}

/** 带旋转的包围盒。 */
export function getBoundWithRotation(el: GfxElement): IBound {
  if (!el.rotate) return { x: el.x, y: el.y, w: el.w, h: el.h };
  const center = centerOf({ x: el.x, y: el.y, w: el.w, h: el.h });
  const rad = (el.rotate * Math.PI) / 180;
  const corners: IVec[] = [
    [el.x, el.y],
    [el.x + el.w, el.y],
    [el.x + el.w, el.y + el.h],
    [el.x, el.y + el.h],
  ].map(c => rotatePoint(c as IVec, center, rad)) as IVec[];
  const xs = corners.map(c => c[0]);
  const ys = corners.map(c => c[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
}

export function getCommonBound(bounds: (IBound | null | undefined)[]): IBound | null {
  const valid = bounds.filter(Boolean) as IBound[];
  if (valid.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const b of valid) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.w);
    maxY = Math.max(maxY, b.y + b.h);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** 端口归一化锚点到绝对坐标。 */
export function anchorToPoint(bound: IBound, anchor: IVec): IVec {
  return [bound.x + bound.w * anchor[0], bound.y + bound.h * anchor[1]];
}

/** 元素上的候选连接锚点（8 方向）。 */
export const ANCHORS: IVec[] = [
  [0.5, 0],
  [1, 0.5],
  [0.5, 1],
  [0, 0.5],
  [0, 0],
  [1, 0],
  [1, 1],
  [0, 1],
];

/** 解析连接线端点：优先绑定元素锚点，否则使用自由坐标。 */
export function resolveEndpoint(
  endpoint: ConnectorEndpoint,
  lookup: (id: string) => GfxElement | undefined
): IVec {
  if (endpoint.id) {
    const el = lookup(endpoint.id);
    if (el) {
      const b: IBound = { x: el.x, y: el.y, w: el.w, h: el.h };
      return anchorToPoint(b, endpoint.anchor ?? [0.5, 0.5]);
    }
  }
  return endpoint.point ?? [0, 0];
}

/** 点到线段距离。 */
export function distanceToSegment(p: IVec, a: IVec, b: IVec): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

/** 折线（正交）路径。 */
export function orthPath(a: IVec, b: IVec): IVec[] {
  const midX = (a[0] + b[0]) / 2;
  return [a, [midX, a[1]], [midX, b[1]], b];
}

/** 生成连接线的顶点序列。 */
export function connectorPoints(
  endpointA: ConnectorEndpoint,
  endpointB: ConnectorEndpoint,
  mode: 'straight' | 'orth',
  lookup: (id: string) => GfxElement | undefined
): IVec[] {
  const a = resolveEndpoint(endpointA, lookup);
  const b = resolveEndpoint(endpointB, lookup);
  return mode === 'orth' ? orthPath(a, b) : [a, b];
}

/** 连接线的包围盒（含外扩）。 */
export function connectorBound(points: IVec[], extension = 12): IBound {
  const xs = points.map(p => p[0]);
  const ys = points.map(p => p[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return {
    x: minX - extension,
    y: minY - extension,
    w: maxX - minX + extension * 2,
    h: maxY - minY + extension * 2,
  };
}

/** 多段线命中测试。 */
export function hitPolyline(p: IVec, points: IVec[], tolerance: number): boolean {
  for (let i = 0; i < points.length - 1; i++) {
    if (distanceToSegment(p, points[i], points[i + 1]) <= tolerance) return true;
  }
  return false;
}

/* ── 联系绳索（悬垂曲线）─────────────────────────────── */

/** 绳索控制点：中点向下垂。 */
export function ropeControl(a: IVec, b: IVec, sag: number): IVec {
  const dist = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2 + dist * sag];
}

/** 采样二次贝塞尔，得到绳索折线。 */
export function ropePoints(a: IVec, b: IVec, sag = 0.12, samples = 28): IVec[] {
  const c = ropeControl(a, b, sag);
  const points: IVec[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const mt = 1 - t;
    points.push([
      mt * mt * a[0] + 2 * mt * t * c[0] + t * t * b[0],
      mt * mt * a[1] + 2 * mt * t * c[1] + t * t * b[1],
    ]);
  }
  return points;
}

/** 两点之间的中点（沿绳索弧线）。 */
export function ropeMidpoint(a: IVec, b: IVec, sag = 0.12): IVec {
  const points = ropePoints(a, b, sag, 4);
  return points[2];
}

/** 稳定的伪随机序列（用于手绘拖动）。 */
export function seededJitter(seed: number, count: number, amplitude: number): number[] {
  const out: number[] = [];
  let state = Math.floor(seed) || 1;
  for (let i = 0; i < count; i++) {
    state = (state * 1664525 + 1013904223) % 4294967296;
    out.push(((state / 4294967296) * 2 - 1) * amplitude);
  }
  return out;
}

/** 手绘风格椭圆采样点（圈注）。 */
export function sketchEllipse(bound: IBound, seed: number, samples = 64): IVec[] {
  const cx = bound.x + bound.w / 2;
  const cy = bound.y + bound.h / 2;
  const rx = bound.w / 2;
  const ry = bound.h / 2;
  const jitter = seededJitter(seed, samples, Math.min(rx, ry) * 0.045);
  const start = -0.35;
  const points: IVec[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = start + (i / samples) * (Math.PI * 2 + 0.7);
    const w = 1 + (jitter[i] ?? 0) / Math.max(1, Math.min(rx, ry));
    points.push([cx + Math.cos(t) * rx * w, cy + Math.sin(t) * ry * w]);
  }
  return points;
}

/** 元素是否与包围盒相交（用于框选）。 */
export function elementIntersectsBound(el: GfxElement, b: IBound, lookup?: (id: string) => GfxElement | undefined): boolean {
  if (el.type === 'connector' || el.type === 'rope') {
    const props = el.props as {
      source?: ConnectorEndpoint;
      target?: ConnectorEndpoint;
      mode?: 'straight' | 'orth';
      sag?: number;
    };
    if (!props.source || !props.target) return false;
    const points =
      el.type === 'rope'
        ? ropePoints(
            resolveEndpoint(props.source, lookup ?? (() => undefined)),
            resolveEndpoint(props.target, lookup ?? (() => undefined)),
            props.sag ?? 0.12
          )
        : connectorPoints(props.source, props.target, props.mode ?? 'straight', lookup ?? (() => undefined));
    return points.some(p => p[0] >= b.x && p[0] <= b.x + b.w && p[1] >= b.y && p[1] <= b.y + b.h);
  }
  const bound = getBoundWithRotation(el);
  return !(
    bound.x + bound.w < b.x ||
    b.x + b.w < bound.x ||
    bound.y + bound.h < b.y ||
    b.y + b.h < bound.y
  );
}